import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement } from 'lit/decorators.js';
import componentStyles from './se-facets.css.lit';
import formStyles from '../se-forms/se-forms.css.lit';
import '../se-accordion/se-accordion';
import '../se-accordion-panel/se-accordion-panel';
import '../../base/outline-icon/outline-icon';
import { MobileController } from '../../controllers/mobile-controller';
import { SlotController } from '../../controllers/slot-controller';

type Check = { label: String; id: String; link: String; isChecked: boolean };
type Filter = {
  groupName: String;
  groupId: String;
  isHidden: Boolean;
  checks: Array<Check>;
};

/**
 * The SeFacets component
 * @element se-facets
 */
@customElement('se-facets')
export class SeFacets extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles, formStyles];
  slots = new SlotController(this, true);
  mobileController = new MobileController(this);
  path = window.location.pathname;
  // Getting state of toggles saved on local storage
  rawToggleStates = window.localStorage.getItem('activeToggles');
  toggleStates = this.rawToggleStates ? JSON.parse(this.rawToggleStates) : null;
  // initial state for toggles in a new path
  initialToggleStates = {
    accordions: {
      [0]: true,
    },
    shows: {
      [0]: false,
    },
  };

  connectedCallback() {
    super.connectedCallback();
    //Setting initialValues
    if (!this.toggleStates) {
      window.localStorage.setItem(
        'activeToggles',
        JSON.stringify({ [this.path]: this.initialToggleStates })
      );
    }
    if (
      !this.toggleStates[this.path] ||
      Object.keys(this.toggleStates[this.path]).join() !==
        ['accordions', 'shows'].join()
    ) {
      this.toggleStates[this.path] = this.initialToggleStates;
      window.localStorage.setItem(
        'activeToggles',
        JSON.stringify(this.toggleStates)
      );
    }
  }

  updated() {
    this.focusLastFilterSelected();
    this.addAccordionsButtonsEventListeners();
  }

  /**
   * When a filter is slected it refresh the page
   * this function set focus on the checkbox of that last selected filter after the refresh
   */
  focusLastFilterSelected() {
    const lastClickedFilterGroup = localStorage.getItem(
      'lastClickedFilterGroup'
    );
    const lastClickedFilterCheck = localStorage.getItem(
      'lastClickedFilterCheck'
    );
    if (lastClickedFilterGroup && lastClickedFilterCheck) {
      const lastClickedElement = this?.shadowRoot?.querySelector(
        `#${lastClickedFilterGroup} #${lastClickedFilterCheck}`
      ) as HTMLElement;
      setTimeout(() => {
        lastClickedElement?.focus();
        localStorage.removeItem('lastClickedFilterGroup');
        localStorage.removeItem('lastClickedFilterCheck');
      }, 0);
    }
  }

  /**
   * Add the events listener to the accordion toggle buttons
   * and calls the setToggleState on it
   */
  addAccordionsButtonsEventListeners() {
    customElements.whenDefined('se-accordion-panel').then(() => {
      setTimeout(() => {
        const accordions =
          this?.shadowRoot?.querySelectorAll('se-accordion-panel');
        accordions?.forEach(accordion => {
          const accordionButton = accordion.shadowRoot?.querySelector(
            'button'
          ) as HTMLElement;
          if (accordionButton.getAttribute('listener') !== 'true') {
            const onAccordionClick = () => {
              const index = accordion.getAttribute('data-index') || '0';
              const type = accordion.getAttribute('data-type') || '';
              this.setToggleState(parseInt(index), type);
            };
            accordionButton?.addEventListener('click', onAccordionClick);
            accordionButton.setAttribute('listener', 'true');
          }
        });
      }, 0);
    });
  }

  render(): TemplateResult {
    return html`
      <div class="se-facets">
        ${this.mobileController.isMobile
          ? html`<se-accordion-panel slot="panels" alt>
              <h5 slot="heading">Filter by</h5>
              <div>${this.filtersTemplate()}</div>
            </se-accordion-panel>`
          : html`<h5 class="se-facets__title">Filter by</h5>
              ${this.filtersTemplate()}`}
      </div>
    `;
  }

  filtersTemplate() {
    const filters = this.slotToData();
    return html` <div id="slotted-facets" class="hidden">
        <slot name="filters"></slot>
      </div>
      ${filters
        ? filters.map((filter, groupIndex) => {
            let isAccordionActive = groupIndex === 0 ? true : false;
            if (this.toggleStates && this.toggleStates[this.path]) {
              isAccordionActive =
                this.toggleStates[this.path].accordions[groupIndex];
            }
            return html` <se-accordion-panel
              id="accordion-${filter.groupId}"
              slot="panels"
              ?active="${isAccordionActive}"
              data-index="${groupIndex}"
              data-type="accordions"
              class="${filter.isHidden ? 'hidden' : ''}"
            >
              <h5 slot="heading">${filter.groupName}</h5>
              <div class="se-facets__filters" id="${filter.groupId}">
                ${filter.checks.map((check, checkIndex) =>
                  this.checkTemplate(check, checkIndex, groupIndex)
                )}
                ${filter.checks.length > 6
                  ? html`<button
                      class="toggle-btn"
                      .onclick="${(event: Event) => {
                        this.toggleShow(event, groupIndex);
                      }}"
                    >
                      ${this.toggleStates &&
                      this.toggleStates[this.path].shows[groupIndex]
                        ? html`Show less<outline-icon
                              library="system"
                              name="dash"
                              size="0.7rem"
                            >
                            </outline-icon>`
                        : html`Show more<outline-icon
                              library="system"
                              name="plus"
                              size="0.7rem"
                            >
                            </outline-icon>`}
                    </button>`
                  : ''}
              </div>
            </se-accordion-panel>`;
          })
        : ''}`;
  }

  /**
   * Saves the state of any toggle (accordions and show more/less)
   * @param index is the index of the group of filters
   * @param type
   */
  setToggleState(index: number, type: string) {
    const prevState = this.toggleStates
      ? this.toggleStates
      : { [this.path]: this.initialToggleStates };
    const state = prevState[this.path][type][index];
    prevState[this.path][type][index] = state === undefined ? true : !state;
    window.localStorage.setItem('activeToggles', JSON.stringify(prevState));
  }

  checkTemplate(check: Check, index: number, groupIndex: number) {
    const toggleState = this.toggleStates
      ? this.toggleStates[this.path].shows[groupIndex]
      : null;

    const toggleClass =
      index > 5 ? `toggleable-checkbox ${!toggleState ? 'hidden' : ''}` : '';

    return html`
      <div
        class="js-form-item form-item js-form-type-checkbox ${toggleClass}"
        .onclick="${this.onFilterClick}"
      >
        <input
          type="checkbox"
          id="${check.id}"
          class="form-checkbox"
          ?checked="${check.isChecked}"
        />
        <label for="${check.id}" class="option font-body">${check.label}</label>
        <a
          href="${check.link}"
          data-drupal-facet-item-id="${check.id}"
          data-drupal-facet-item-value="${check.label}"
          class="hidden"
        >
          ${check.label}
        </a>
      </div>
    `;
  }

  /**
   * Handle the click event on filters
   * @param event click Event
   */
  onFilterClick(event: Event) {
    const check = event.target as HTMLElement;
    const checkboxContainer = check.parentElement as HTMLElement;
    const link = checkboxContainer.querySelector('a');
    const lastClickedGroupId = checkboxContainer.parentElement?.id || '';
    if (check.id && check.id !== '') {
      localStorage.setItem('lastClickedFilterGroup', lastClickedGroupId);
      localStorage.setItem('lastClickedFilterCheck', check.id);
      link?.click();
    }
  }

  /**
   * Toggle the display of the filters when show more/less is clicked
   * @param event click event
   * @param index index of the group of filters
   */
  toggleShow(event: Event, index: number) {
    const targetElement = event.target as HTMLElement;
    const toggleButton = (
      targetElement.nodeName !== 'OUTLINE-ICON'
        ? targetElement
        : targetElement.parentElement
    ) as HTMLElement;
    const checkContainer = toggleButton.parentElement;
    const toggleableChecks = checkContainer?.querySelectorAll(
      '.toggleable-checkbox'
    );
    this.setToggleState(index, 'shows');
    toggleableChecks?.forEach(check => {
      check.classList.toggle('hidden');
    });
    if (toggleButton.innerText === 'Show more') {
      toggleButton.innerHTML = `Show less
        <outline-icon library="system" name="dash" size="0.7rem">
        </outline-icon>`;
    } else {
      toggleButton.innerHTML = `Show more
        <outline-icon library="system" name="plus" size="0.7rem">
        </outline-icon>`;
    }
  }

  /**
   * This function takes the filter slot and transforms it
   * into an array of filter groups to fill the component's data.
   * @returns an array with the filter separated by groups
   */
  slotToData(): Array<Filter> {
    const slotContent = this.shadowRoot?.querySelector(
      'div[cloned-slot-name="filters"]'
    ) as HTMLSlotElement;
    const filters: Array<Filter> = [];
    if (slotContent) {
      const groups = slotContent.querySelectorAll('.block-facet--checkbox');

      [...groups].forEach(group => {
        const filter = this.groupElementToFilterGroupObject(group);
        filters.push(filter);
      });
    }
    return filters;
  }

  /**
   * This function takes a group element and transforms it
   * into a filter group object
   * @param group
   * @returns a filter group object
   */
  groupElementToFilterGroupObject(group: Element) {
    const groupName = group.querySelector('outline-heading')?.innerText;
    const groupId = group
      .querySelector('ul')
      ?.getAttribute('data-drupal-facet-id');
    const isHidden = group.classList.contains('hidden');
    const checks: Array<Check> = [];
    const checkList = group.querySelectorAll('.facet-item');
    [...checkList].forEach(checkElement => {
      const check = this.checkElementToCheckObject(
        checkElement as HTMLElement,
        groupId || ''
      );
      checks.push(check);
    });
    return {
      groupName: groupName || '',
      groupId: groupId || '',
      isHidden: isHidden || false,
      checks,
    };
  }

  /**
   * This function takes a check element and transforms it
   * into a check object
   * @param checkElement
   * @param groupId
   * @returns a check object
   */
  checkElementToCheckObject(checkElement: HTMLElement, groupId: string) {
    const facetItemValue = checkElement.querySelector(
      '.facet-item__value'
    ) as HTMLElement;
    const facetItemLink = checkElement?.querySelector('a');
    const label = facetItemValue?.innerText;
    const id = `${groupId}-${label.replace(/ /g, '-').toLowerCase()}-checkbox`;
    const isChecked = facetItemLink?.querySelector(
      '.facet-item__status.js-facet-deactivate'
    );

    return {
      label: label || '',
      id: id || '',
      link: facetItemLink?.getAttribute('href') || '',
      isChecked: !!isChecked,
    };
  }
}
